import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { Cake, ClipboardList, Copy, Settings, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MemberPhoto } from '@/components/ui/member-photo';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { queryKeys } from '@/lib/query-client';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { MemberQuickViewPanel } from '@/pages/people/member-quick-view-panel';
import { MembersDirectoryToolbar } from '@/pages/people/members-directory-toolbar';
import { MembersLetterRail } from '@/pages/people/members-letter-rail';
import {
  churchMonthDateRange,
  memberDetailPath,
  membersListPath,
  membersListToApiQuery,
} from '@/pages/people/members-list-state';
import {
  loadMembersDirectoryPreferences,
  persistMembersDirectoryPreferences,
} from '@/pages/people/members-directory-preferences';
import { PeopleSubnav } from '@/pages/people/people-subnav';
import { useMediaQuery, useMembersListState } from '@/pages/people/use-members-list-state';
import { buildProfileOpenHandler, useMembersKeyboard } from '@/pages/people/use-members-keyboard';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatNumber } from '@/utils/format';
import { cn } from '@/utils/cn';

import { MemberCreateDialog } from './member-create-dialog';
import { formToPayload, type MemberFormState, type MemberRecord } from './member-form-state';
import { MemberImportDialog } from './member-import-dialog';
import { PeopleSettingsDialog } from './people-settings-dialog';

interface PeopleInsights {
  activeMembers: number;
  newInPeriod: number;
  firstTimersInPeriod: number;
  openFollowUps: number;
  byGender: Array<{ key: string; value: number }>;
  byType: Array<{ key: string; value: number }>;
  birthdaysToday: Array<{ id: string; firstName: string; lastName: string; dateOfBirth: string }>;
  birthdaysThisMonth: Array<{
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  }>;
}

const givenNames = (row: MemberRecord) =>
  [row.firstName, row.middleName].filter(Boolean).join(' ');

const MEMBERS_SEARCH_ID = 'members-directory-search';

export const MembersPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone);
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');

  const { state, patchState, resetFilters } = useMembersListState();
  const [splitView, setSplitView] = useState(() => loadMembersDirectoryPreferences(church?.id).splitView);

  const canImport = hasPermission(membership, PERMISSIONS.PEOPLE_IMPORT);
  const canViewDuplicates = hasPermission(membership, PERMISSIONS.PEOPLE_VIEW);
  const canCreate = hasPermission(membership, PERMISSIONS.PEOPLE_CREATE);
  const canEditSettings = hasPermission(membership, PERMISSIONS.PEOPLE_EDIT);

  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get('import') === '1' && canImport) {
      setImportOpen(true);
    }
  }, [canImport, searchParams]);

  useEffect(() => {
    persistMembersDirectoryPreferences(church?.id, {
      ...loadMembersDirectoryPreferences(church?.id),
      splitView,
    });
  }, [church?.id, splitView]);

  const apiQuery = useMemo(() => membersListToApiQuery(state), [state]);

  const list = useQuery({
    queryKey: queryKeys.list(church?.id ?? null, 'people/members', apiQuery),
    queryFn: () =>
      api.get<{ data: MemberRecord[]; page: { page: number; totalPages: number; totalItems: number } }>(
        `/people/members?${new URLSearchParams(apiQuery).toString()}`,
      ),
    enabled: Boolean(church?.id),
  });

  const insights = useQuery({
    queryKey: queryKeys.list(church?.id ?? null, 'people/insights'),
    queryFn: () => api.get<PeopleInsights>('/people/insights'),
    enabled: Boolean(church?.id),
  });

  const create = useMutation({
    mutationFn: (form: MemberFormState) => api.post<MemberRecord>('/people/members', formToPayload(form)),
    onSuccess: (created) => {
      toast.success(t('members.form.saved'));
      setOpen(false);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.list(church?.id ?? null, 'people/members'),
      });
      void navigate(memberDetailPath(created.id, state));
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = list.data?.data ?? [];
  const showSplitPreview = isLargeScreen && splitView;
  const selectedRow = rows.find((row) => row.id === state.selectedId) ?? null;

  const openProfile = buildProfileOpenHandler((path) => void navigate(path), state);

  const handleRowActivate = (row: MemberRecord) => {
    if (showSplitPreview) {
      patchState({ selectedId: row.id });
      return;
    }
    openProfile(row.id);
  };

  useMembersKeyboard({
    enabled: true,
    rows,
    selectedId: state.selectedId,
    onSelect: (memberId) => patchState({ selectedId: memberId }),
    onOpenProfile: openProfile,
    searchInputId: MEMBERS_SEARCH_ID,
  });

  const monthRange = churchMonthDateRange(timezone);

  const columns: DataTableColumn<MemberRecord>[] = [
    {
      id: 'lastName',
      header: t('members.columns.lastName'),
      width: showSplitPreview ? '15rem' : undefined,
      cell: (row) =>
        showSplitPreview ? (
          <span className="inline-flex max-w-full items-center gap-2.5 font-medium">
            <MemberPhoto src={row.photoUrl} name={row.lastName} size="sm" />
            <span>{row.lastName}</span>
          </span>
        ) : (
          <MemberNameLink
            memberId={row.id}
            name={row.lastName}
            photoUrl={row.photoUrl}
            showPhoto
            className="font-medium"
          />
        ),
    },
    {
      id: 'givenNames',
      header: t('members.columns.givenNames'),
      width: showSplitPreview ? '6.5rem' : undefined,
      cell: (row) => givenNames(row) || '—',
    },
    {
      id: 'type',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('members.columns.type')}
          <Tip tipId="members.columns.type" />
        </span>
      ),
      cell: (row) => t(`members.type.${row.memberType}`),
      hideBelowMd: true,
    },
    {
      id: 'gender',
      header: t('members.columns.gender'),
      cell: (row) => (row.gender ? t(`members.gender.${row.gender}`) : '—'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('members.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'active' ? 'success' : 'neutral'}>
          {t(`members.status.${row.status}`)}
        </Badge>
      ),
    },
    ...(showSplitPreview
      ? []
      : [
          {
            id: 'appAccount',
            header: t('members.columns.appAccount'),
            cell: (row: MemberRecord) => (
              <Badge variant={row.userId ? 'success' : 'neutral'}>
                {row.userId
                  ? t('members.appAccount.connected')
                  : t('members.appAccount.notConnected')}
              </Badge>
            ),
            hideBelowMd: true,
          } satisfies DataTableColumn<MemberRecord>,
        ]),
    {
      id: 'phone',
      header: t('members.columns.phone'),
      cell: (row) =>
        row.contactHidden ? t('members.contactHidden') : (row.phone ?? '—'),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('members.title')}
        description={t('members.description')}
        tipId="members.directory.purpose"
        headerActions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canEditSettings ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={t('members.settings.open')}
                onClick={() => setSettingsOpen(true)}
              >
                <Settings className="size-4" aria-hidden />
              </Button>
            ) : null}
            {canViewDuplicates ? (
              <Button variant="outline" onClick={() => navigate('/people/duplicates')}>
                <Copy className="mr-2 size-4" />
                {t('duplicates.action')}
              </Button>
            ) : null}
            {canImport ? (
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                {tCommon('action.import')}
              </Button>
            ) : null}
            {canCreate ? (
              <Button onClick={() => setOpen(true)}>
                {t('members.create')}
              </Button>
            ) : null}
          </div>
        }
      />
      <PeopleSubnav />

      <CollapsibleInsights
        summary={[
          {
            label: t('members.insights.active'),
            value: insights.data ? formatNumber(insights.data.activeMembers) : '…',
            href: membersListPath({ ...state, statuses: ['members'], page: 1, selectedId: null }),
          },
          {
            label: t('members.insights.newInPeriod'),
            value: insights.data ? formatNumber(insights.data.newInPeriod) : '…',
            href: membersListPath({
              ...state,
              statuses: [],
              joinedFrom: monthRange.from,
              joinedTo: monthRange.to,
              page: 1,
              selectedId: null,
            }),
          },
          {
            label: t('members.insights.firstTimers'),
            value: insights.data ? formatNumber(insights.data.firstTimersInPeriod) : '…',
            href: membersListPath({
              ...state,
              statuses: ['firstTimers'],
              joinedFrom: monthRange.from,
              joinedTo: monthRange.to,
              page: 1,
              selectedId: null,
            }),
          },
          {
            label: t('members.insights.openFollowUps'),
            value: insights.data ? formatNumber(insights.data.openFollowUps) : '…',
            href: '/people/follow-ups',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('members.insights.active')}
            tipId="members.insights.active"
            value={insights.data ? formatNumber(insights.data.activeMembers) : undefined}
            icon={<Users className="size-4" aria-hidden />}
            loading={insights.isLoading}
            to={membersListPath({ ...state, statuses: ['members'], page: 1, selectedId: null })}
          />
          <StatCard
            label={t('members.insights.newInPeriod')}
            tipId="members.insights.newInPeriod"
            value={insights.data ? formatNumber(insights.data.newInPeriod) : undefined}
            icon={<UserPlus className="size-4" aria-hidden />}
            loading={insights.isLoading}
            to={membersListPath({
              ...state,
              statuses: [],
              joinedFrom: monthRange.from,
              joinedTo: monthRange.to,
              page: 1,
              selectedId: null,
            })}
          />
          <StatCard
            label={t('members.insights.firstTimers')}
            tipId="members.insights.firstTimers"
            value={insights.data ? formatNumber(insights.data.firstTimersInPeriod) : undefined}
            icon={<UserPlus className="size-4" aria-hidden />}
            loading={insights.isLoading}
            to={membersListPath({
              ...state,
              statuses: ['firstTimers'],
              joinedFrom: monthRange.from,
              joinedTo: monthRange.to,
              page: 1,
              selectedId: null,
            })}
          />
          <StatCard
            label={t('members.insights.openFollowUps')}
            tipId="members.insights.openFollowUps"
            value={insights.data ? formatNumber(insights.data.openFollowUps) : undefined}
            icon={<ClipboardList className="size-4" aria-hidden />}
            loading={insights.isLoading}
            to="/people/follow-ups"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <ChartCard
            title={t('members.insights.byGender')}
            loading={insights.isLoading}
            hasData={(insights.data?.byGender.length ?? 0) > 0}
            empty={{ title: t('members.insights.breakdownEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(insights.data?.byGender ?? []).map((row) => ({
                label: t(`members.gender.${row.key}`, { defaultValue: row.key }),
                value: row.value,
              }))}
              formatValue={formatNumber}
              size={140}
            />
          </ChartCard>
          <ChartCard
            title={t('members.insights.byType')}
            loading={insights.isLoading}
            hasData={(insights.data?.byType.length ?? 0) > 0}
            empty={{ title: t('members.insights.breakdownEmpty') }}
            height={180}
          >
            <SimpleDonutChart
              data={(insights.data?.byType ?? []).map((row) => ({
                label: t(`members.type.${row.key}`, { defaultValue: row.key }),
                value: row.value,
              }))}
              formatValue={formatNumber}
              size={140}
            />
          </ChartCard>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-body font-medium">
                <Cake className="size-4 text-fg-tertiary" aria-hidden />
                {t('members.insights.birthdaysToday')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights.isLoading ? (
                <p className="text-body-sm text-fg-muted">…</p>
              ) : (insights.data?.birthdaysToday.length ?? 0) === 0 ? (
                <p className="text-body-sm text-fg-muted">{t('members.insights.birthdaysTodayEmpty')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {(insights.data?.birthdaysToday ?? []).map((person) => (
                    <li key={person.id}>
                      <Link
                        to={memberDetailPath(person.id, state)}
                        className="font-medium text-brand hover:underline"
                      >
                        {[person.firstName, person.lastName].filter(Boolean).join(' ')}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {(insights.data?.birthdaysThisMonth.length ?? 0) > 0 ? (
                <p className="mt-4 text-caption text-fg-tertiary">
                  {t('members.insights.birthdaysMonth')}:{' '}
                  <Link to="/people/birthdays" className="text-brand hover:underline">
                    {formatNumber(insights.data!.birthdaysThisMonth.length)}
                  </Link>
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </CollapsibleInsights>

      <div className="sticky top-0 z-20 -mx-4 mb-4 border-b border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <MembersDirectoryToolbar
          state={state}
          onPatch={patchState}
          onResetFilters={resetFilters}
          splitView={splitView}
          onSplitViewChange={setSplitView}
          searchInputId={MEMBERS_SEARCH_ID}
        />
      </div>

      <div className={cn('flex gap-4', showSplitPreview && 'lg:items-start')}>
        <MembersLetterRail
          value={state.letter}
          onChange={(letter) => patchState({ letter, page: 1, selectedId: null })}
          className="hidden shrink-0 xl:flex"
        />

        <div className={cn('min-w-0 flex-1', showSplitPreview && 'lg:max-w-[67%]')}>
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('members.empty')}</p>}
            onRowClick={handleRowActivate}
            highlightedRowKey={showSplitPreview ? state.selectedId : null}
          />

          {list.data?.page && list.data.page.totalPages > 1 ? (
            <div className="mt-4">
              <Pagination
                page={list.data.page.page}
                pageSize={state.pageSize}
                totalItems={list.data.page.totalItems}
                totalPages={list.data.page.totalPages}
                onPageChange={(page) => patchState({ page, selectedId: null })}
                labels={{
                  showing: ({ from, to, total }) => `${from}–${to} / ${total}`,
                  perPage: tCommon('pagination.perPage', { defaultValue: 'Per page' }),
                  first: tCommon('pagination.first', { defaultValue: 'First' }),
                  previous: tCommon('pagination.previous', { defaultValue: 'Previous' }),
                  next: tCommon('pagination.next', { defaultValue: 'Next' }),
                  last: tCommon('pagination.last', { defaultValue: 'Last' }),
                }}
              />
            </div>
          ) : null}
        </div>

        {showSplitPreview ? (
          <div className="hidden min-h-0 min-w-0 flex-1 lg:sticky lg:top-[4.5rem] lg:z-10 lg:max-w-[33%] lg:self-start lg:block">
            <MemberQuickViewPanel
              memberId={state.selectedId}
              previewName={
                selectedRow
                  ? [selectedRow.firstName, selectedRow.middleName, selectedRow.lastName]
                      .filter(Boolean)
                      .join(' ')
                  : undefined
              }
              listState={state}
            />
          </div>
        ) : null}
      </div>

      <p className="mt-4 text-caption text-fg-tertiary">{t('members.keyboardHints')}</p>

      <MemberCreateDialog
        open={open}
        onOpenChange={setOpen}
        submitting={create.isPending}
        onSubmit={async (form) => {
          await create.mutateAsync(form);
        }}
      />

      <MemberImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultCountry={(church?.country as CountryCode | undefined) ?? 'GH'}
      />

      <PeopleSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
};
